/**
 * A place's standings (place manager, unit 4): each season's top ten, and the
 * leader for the Leaderboard card. The reads come in through
 * {@link StandingsApi}, so the picking is unit-testable without a network; the
 * real read lives in `place-standings-api.ts`.
 *
 * Standings are per season: the backend has no place-wide leaderboard, so the
 * page says a roll-up comes later and never adds seasons together.
 *
 * Which seasons: the same ones "How the last ones went" and the members read
 * (unit 2's {@link seasonsToRead}: of those that have begun, the most recently
 * running), from the one seasons read the sections share. The leader is the
 * first of them: the season running most recently.
 *
 * Two steps, as the members: {@link readFirstStandings} reads the first
 * season's table alone (the card and the summary need it); the others are read
 * by {@link readOtherStandings} only when the partner opens the standings.
 */
import type { PlaceSeasonsData } from "@/data/place-events-api";
import type { SeasonStandingsData } from "@/data/place-standings-api";
import { faceOf, type Face } from "@/features/places/face";
import { seasonsToRead } from "@/features/places/last-events";

/** How many of a season's best a table shows (the read asks for one more). */
export const STANDINGS_SHOWN = 10;

type Season = PlaceSeasonsData["seasons"]["nodes"][number];

/** The read this section makes (`place-standings-api.ts`, with the viewer
 *  bound). Null: the season is not readable by this viewer. */
export interface StandingsApi {
  /** A season's best, first {@link STANDINGS_SHOWN} + 1 of them. */
  standings(seasonId: string): Promise<SeasonStandingsData | null>;
}

/** One season as read: its rankings, not readable by this viewer, the read
 *  failed, or not read yet (`undefined`: the seasons after the first wait
 *  until the partner opens the standings). */
export interface SeasonStandingsRead {
  season: Season;
  rankings?: SeasonStandingsData | null | "failed";
}

/** One line of a season's table: the backend's rank and score, as given. */
export interface StandingRow {
  rank: number;
  face: Face;
  score: number;
}

/** One season's standings, as the section draws them. */
export interface SeasonStandings {
  id: string;
  title: string;
  /** The season is over: its standings are final. */
  ended: boolean;
  /** "shown": read; "hidden": not shown to this viewer; "failed": did not
   *  read; "unread": not read yet (read when the standings are opened). */
  state: "shown" | "hidden" | "failed" | "unread";
  /** The top {@link STANDINGS_SHOWN}, best first; empty unless shown. */
  rows: StandingRow[];
  /** More have scored than the rows show. */
  more: boolean;
  /** People scored, but rank 1 did not come back (an account that did not
   *  load, or only ranks past the top ten came back). */
  firstMissing: boolean;
}

/** The leader of the season running most recently, for the Leaderboard card. */
export interface Leader {
  face: Face;
  score: number;
  season: string;
  /** The season is over: they finished first, rather than lead. */
  ended: boolean;
}

/** What the section and the Leaderboard card draw for one place. */
export interface PlaceStandings {
  /** One per season read, in {@link seasonsToRead}'s order. */
  groups: SeasonStandings[];
  /** The tables after the first: not asked for yet, being read, or read. */
  tables: "unread" | "reading" | "read";
  /** Seasons read whose standings are not shown to this viewer. */
  notShown: number;
  /** Seasons read, of all the place's seasons this viewer sees; `cut` when
   *  some that have begun were not read (older, or past the first listing).
   *  Seasons still to come have no standings, so they never cut. */
  seasons: { read: number; total: number; cut: boolean };
}

function groupOf({ season, rankings }: SeasonStandingsRead, now: number): SeasonStandings {
  const base = { id: season.id, title: season.title, ended: season.endAt < now, rows: [], more: false, firstMissing: false };
  if (rankings === undefined) return { ...base, state: "unread" };
  if (rankings === "failed") return { ...base, state: "failed" };
  if (rankings === null) return { ...base, state: "hidden" };
  // By rank, as the backend gives it (a skipped account leaves a gap, never a
  // renumbering). Sorted here: the backend's batch read has no outer order.
  const rows = rankings
    .filter((r) => r.rank <= STANDINGS_SHOWN)
    .sort((a, b) => a.rank - b.rank)
    .map((r) => ({ rank: r.rank, face: faceOf(r.user), score: r.score }));
  const more = rankings.some((r) => r.rank > STANDINGS_SHOWN);
  const firstMissing = (rows.length > 0 || more) && rows[0]?.rank !== 1;
  return { ...base, state: "shown", rows, more, firstMissing };
}

/** The section's model from what was read. `reading`: the tables after the
 *  first are being read. */
export function placeStandingsOf(
  seasons: PlaceSeasonsData,
  reads: readonly SeasonStandingsRead[],
  now: number,
  reading = false,
): PlaceStandings {
  const groups = reads.map((r) => groupOf(r, now));
  const { totalCount: total, nodes } = seasons.seasons;
  // Unlisted seasons began before every listed one, so they may have standings.
  const cut = total > nodes.length || nodes.filter((s) => s.startAt <= now).length > reads.length;
  return {
    groups,
    tables: reading ? "reading" : groups.some((g) => g.state === "unread") ? "unread" : "read",
    notShown: groups.filter((g) => g.state === "hidden").length,
    seasons: { read: reads.length, total, cut },
  };
}

/** The Leaderboard card's leader: whoever is ranked 1 in the season running
 *  most recently (the first group). Null when that season has no one ranked,
 *  is not shown, or its first place did not come back (an account that did not
 *  load): rank 2 is never called the leader. */
export function leaderOf(data: PlaceStandings): Leader | null {
  const first = data.groups[0];
  const top = first?.state === "shown" ? first.rows[0] : undefined;
  return top?.rank === 1 ? { face: top.face, score: top.score, season: first!.title, ended: first!.ended } : null;
}

/** The backend's error for a season that has no leaderboard row
 *  (`Season.leaderboard` is non-null, so its resolver throws). */
const NO_LEADERBOARD = "No leaderboard found";

/** A season's rankings from `read`: none (empty) when the season has no
 *  leaderboard row, a season with no standings rather than a failed read; any
 *  other error is thrown on (the read did not read). */
export async function rankingsOrNone(read: Promise<SeasonStandingsData | null>): Promise<SeasonStandingsData | null> {
  try {
    return await read;
  } catch (e) {
    if (e instanceof Error && e.message.includes(NO_LEADERBOARD)) return [];
    throw e;
  }
}

/** One season's rankings; a read that throws is said as "failed". */
async function readOne(season: Season, api: StandingsApi): Promise<SeasonStandingsRead> {
  try {
    return { season, rankings: await api.standings(season.id) };
  } catch {
    return { season, rankings: "failed" };
  }
}

/** The first read: the standings of the season running most recently only
 *  (the Leaderboard card and the section's summary need it), from the seasons
 *  read once and shared with units 2 and 3. The other seasons come back not
 *  read yet, for {@link readOtherStandings}. */
export async function readFirstStandings(seasons: PlaceSeasonsData, api: StandingsApi, now: number): Promise<SeasonStandingsRead[]> {
  const [first, ...rest] = seasonsToRead(seasons.seasons.nodes, now);
  if (!first) return [];
  return [await readOne(first, api), ...rest.map((season) => ({ season }))];
}

/** The other seasons' tables, side by side, read when the partner opens the
 *  standings (once per visit). A season whose read fails is said in its own
 *  group and leaves the others standing; seasons already read are kept. */
export async function readOtherStandings(reads: readonly SeasonStandingsRead[], api: StandingsApi): Promise<SeasonStandingsRead[]> {
  return Promise.all(reads.map((r) => (r.rankings === undefined ? readOne(r.season, api) : r)));
}

/** Every season's standings at once: the first read, then the others. */
export async function readStandings(seasons: PlaceSeasonsData, api: StandingsApi, now: number): Promise<SeasonStandingsRead[]> {
  return readOtherStandings(await readFirstStandings(seasons, api, now), api);
}
